import { useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useShopFlow } from "../constants/ShopFlow";
import TopAnnouncementBar from "@/components/navbar/TopAnnouncementBar";
import LogoBar from "@/components/navbar/LogoBar";
import SearchBarDekstop from "@/components/navbar/SearchBarDekstop";
import SearchBarMobile from "@/components/navbar/SearchBarMobile";
import type { LangType } from "@/types/layout-type";
import ThemeAndLanguage from "@/components/navbar/ThemeAndLanguage";
import NavbarIcons from "@/components/navbar/NavbarIcons";
import NavbarLinks from "@/components/navbar/NavbarLinks";
import MobileMenuDropdown from "@/components/navbar/MobileMenuDropdown";

export default function Navbar() {
    const { i18n } = useTranslation();
    const { lang } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const baseRoute = `/${lang || "id"}`;
    const isShopFlow = useShopFlow();

    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const languages: LangType[] = [
        { code: "id", label: "Indonesia" },
        { code: "en", label: "English" },
        { code: "my", label: "Melayu" },
    ];

    // Theme handle
    const [isDark, setIsDark] = useState(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            document.documentElement.classList.add("dark");
            return true;
        } else {
            document.documentElement.classList.remove("dark");
            return false;
        }
    });

    // Handle change Language
    const changeLanguage = (newLang: string) => {
        i18n.changeLanguage(newLang);
        localStorage.setItem("preferredLang", newLang);
        const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`);
        navigate(newPath + location.search, {
            replace: true,
            state: location.state,
        });
    };

    // Handle Menu
    const toggleMenu = () => setIsOpen(!isOpen);
    const toggleTheme = () => {
        if (isDark) {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
            setIsDark(false);
        } else {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
            setIsDark(true);
        }
    };

    const handleSearch = (value: string) => {
        if (value.trim()) {
            navigate(
                `${baseRoute}/shop?search=${encodeURIComponent(value.trim())}`,
            );
        } else {
            navigate(`${baseRoute}/shop`);
        }
    };

    return (
        <div
            className={`w-full ${isShopFlow ? "hidden md:flex" : "flex"} flex-col z-50 fixed top-0 left-0 right-0 bg-transparent transition-all duration-300 pointer-events-none`}>
            {/* Level 1 */}
            <TopAnnouncementBar />

            {/* Level 2 */}
            <div className="flex justify-between items-center px-4 lg:px-8 py-3 lg:py-4 bg-white/90 dark:bg-dark/90 backdrop-blur-md border-b border-gray-100 dark:border-dark-2 relative z-20 pointer-events-auto">
                <LogoBar />
                <SearchBarDekstop handleSearch={handleSearch} />
                <div className="flex items-center gap-4 sm:gap-6">
                    <ThemeAndLanguage
                        languages={languages}
                        isDark={isDark}
                        changeLanguage={changeLanguage}
                        setScrolled={setScrolled}
                        toggleTheme={toggleTheme}
                    />
                    <NavbarIcons />

                    {/* Mobile Menu Toggle */}
                    <button
                        className="md:hidden p-1 text-dark dark:text-white"
                        aria-label="Toggle mobile menu"
                        onClick={toggleMenu}>
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>
            {/* Level 3 */}
            <NavbarLinks scrolled={scrolled} />
            <SearchBarMobile scrolled={scrolled} handleSearch={handleSearch} />
            <MobileMenuDropdown
                isOpen={isOpen}
                isDark={isDark}
                languages={languages}
                toggleTheme={toggleTheme}
                changeLanguage={changeLanguage}
                onClose={toggleMenu}
            />
        </div>
    );
}
